<template>
  <div v-if="draftEvent || pauseEvent">
    <div class="font-bold p-2">Seats</div>
    <div v-if="fullRowOrColumnSelected || fullBlocksSelected" class="flex gap-2 px-2 text-gray-300">
      <SMButton
        v-if="!hasBookedSeatsInSelection && (draftEvent || pauseEvent) && fullBlocksSelected"
        v-tippy="'Duplicate'"
        icon="copy"
        :on-click="duplicate"
      />
      <SMButton
        v-if="fullRowOrColumnSelected && !fullBlocksSelected"
        v-tippy="'Split Block'"
        icon="split"
        :on-click="splitBlock"
      />
      <SMButton v-if="fullBlocksSelected" v-tippy="'Flip Horizontally'" icon="flip-h" :on-click="flipHorizontally" />
      <SMButton v-if="fullBlocksSelected" v-tippy="'Flip Vertically'" icon="flip-v" :on-click="flipVertically" />
      <SMButton v-if="fullBlocksSelected" v-tippy="'Delete Block'" icon="delete" :on-click="removeBlocks" />
    </div>

    <ul
      v-if="!hasOnlyUnavailableSeatsSelected"
      class="space-y-2 px-2 py-1 grid gap-2"
      style="grid-template-columns: auto 1fr"
    >
      <li class="contents">
        <SMIcon icon="visible" />
        <SMCheckbox v-model="seatsVisible" class="md-list-action">Visible</SMCheckbox>
      </li>
      <li class="contents">
        <SMIcon icon="eye_off" class="text-sm" />
        <SMCheckbox v-model="seatsObstructed" class="md-list-action">Restricted View</SMCheckbox>
      </li>
    </ul>

    <div class="md-subheading px-2 my-2 bg-gray-200">Attributes</div>
    <ul class="space-y-2 px-2 py-1">
      <li>
        <SMInput v-model="meta1" v-tippy="'Meta 1'" icon="1" :placeholder="meta1Label" @change="onChangeMeta1" />
      </li>

      <li>
        <SMInput v-model="meta2" v-tippy="'Meta 2'" icon="2" :placeholder="meta2Label" @change="onChangeMeta2" />
      </li>
    </ul>

    <div
      v-if="moreThanOneSeatSelected && fullBlocksSelected && (isDraft || isPaused)"
      class="md-subheading px-2 my-2 bg-gray-200"
    >
      Seat / Row Layout
    </div>
    <ul v-if="fullBlocksSelected && (isDraft || isPaused)" class="space-y-2 p-2">
      <li class="flex">
        <SMNumberInput
          v-tippy="'Rows'"
          :model-value="rows"
          icon="add-row"
          class="w-full"
          :min="1"
          @update:modelValue="onRowsChange"
        />
      </li>
      <li class="flex">
        <SMNumberInput
          v-tippy="'Columns'"
          :model-value="cols"
          icon="add-column"
          class="w-full"
          :min="1"
          @update:modelValue="onColsChange"
        />
      </li>
    </ul>

    <div v-if="moreThanOneSeatSelected && fullBlocksSelected" class="md-subheading px-2 my-2 bg-gray-200">Styles</div>
    <ul class="space-y-2 p-2">
      <li v-if="moreThanOneSeatSelected && fullBlocksSelected" class="flex">
        <SMNumberInput
          v-model="rowSpacing"
          v-tippy="'Row Spacing'"
          :disabled="rowSpacingDisabled"
          icon="row-spacing"
          class="w-full"
          @update:modelValue="onRowSpacingChange"
        />
      </li>
      <li v-if="moreThanOneSeatSelected && fullBlocksSelected" class="flex">
        <SMNumberInput
          v-model="seatSpacing"
          v-tippy="'Seat Spacing'"
          icon="seat-spacing"
          class="w-full"
          @update:modelValue="onSeatSpacingChange"
        />
      </li>
      <li v-if="moreThanOneSeatSelected && fullBlocksSelected" class="flex">
        <SMNumberInput
          v-tippy="'Skew'"
          :model-value="skew.value"
          icon="skew"
          class="w-full"
          :min="-5"
          :max="5"
          @update:modelValue="onSkewValueChange"
        />
      </li>
      <li v-if="fullBlocksSelected" class="flex">
        <SMNumberInput
          v-model="curve.value"
          v-tippy="'Curve'"
          icon="curve"
          class="w-full"
          :min="-300"
          :max="300"
          @update:modelValue="onCurveValueChange"
        />
      </li>
    </ul>
  </div>
</template>
<script lang="ts">
/* eslint-disable */
import eventbus from 'tiny-emitter/instance';
import forEach from 'lodash/forEach';
import uniq from 'lodash/uniq';
import map from 'lodash/map';
import first from 'lodash/first';
import groupBy from 'lodash/groupBy';
import SMInput from '@/components/ui/SMInput.vue';
import SMButton from '@/components/ui/SMButton.vue';
import SMIcon from '@/components/ui/SMIcon.vue';
import SMNumberInput from '@/components/ui/SMNumberInput.vue';
import SMCategorySelect from '@/components/ui/SMCategorySelect.vue';

import { FLIP, EVENTSTATUS, SEATSTATUS, TYPE } from '@/constants';
import SMCheckbox from '@/components/ui/legacy/SMCheckbox.vue';

export default {
  components: {
    SMInput,
    SMButton,
    SMIcon,
    SMNumberInput,
    SMCategorySelect,
    SMCheckbox,
  },
  data() {
    return {
      curveLabel: '',
      skewLabel: '',
      skew: {
        tooltip: false,
        width: 200,
        value: 0,
        min: -5,
        max: 5,
        interval: 1,
      },
      curve: {
        tooltip: false,
        width: 200,
        value: 0,
        min: -300,
        max: 300,
        interval: 10,
      },
      rowSpacing: 0,
      seatSpacing: 0,
      rows: 1,
      cols: 1,
      // rowSpacing: 0,
      // seatSpacing: 0,
    };
  },
  props: ['units'],
  created() {
    const curves = map(this.units, (unit) => unit.attributes.curve);
    const skews = map(this.units, (unit) => unit.attributes.skew);
    const uniqcurves = uniq(curves);
    const uniqskews = uniq(skews);

    this.rowSpacing = this.getRowSpacing();
    this.seatSpacing = this.getSeatSpacing();

    //if multiple curve values, set curve slider to 0.
    if (uniqcurves.length > 1) {
      this.curve.value = 0;
      this.curveLabel = 'Multiple values assigned';
    } else {
      this.curve.value = uniqcurves[0];
    }
    //if multiple skew values, set skew slider to 0.
    if (uniqskews.length > 1) {
      this.skew.value = 0;
      this.skewLabel = 'Multiple values assigned';
    } else {
      this.skew.value = uniqskews[0];
    }

    this.rows = Object.keys(this.groupedRows).length;
    this.cols = this.getNumberOfColumns();
  },
  computed: {
    isDraft() {
      return this.$app.props.status === EVENTSTATUS.DRAFT;
    },
    isPaused() {
      return this.$app.props.status === EVENTSTATUS.PAUSED;
    },
    fullBlocksSelected() {
      return Object.values(groupBy(this.units, (unit) => unit.block.uuid)).every(
        (group) => group.length === group[0].block.seats.length
      );
    },
    seatsVisible: {
      get: function () {
        return this.units.some((unit) => unit.attributes.invisible !== true);
      },
      set: function (newValue) {
        this.units.forEach((unit) => {
          newValue ? unit.makeVisible() : unit.makeInvisible();
        });
        eventbus.emit('update:invisibleSeats');
        const { seats, units, sections } = this.$app.seatmap;
        eventbus.emit('seatmap:updated', seats, units, sections);
        eventbus.emit('selection:updated', this.$app.seatmap.selection);
        this.$app.seatmap.triggerUnsavedChanges(true);
      },
    },
    seatsObstructed: {
      get: function () {
        return this.units.some((unit) => unit.attributes.obstructed === true);
      },
      set: function (newValue: boolean) {
        this.units.forEach((s) => (s.obstructed = newValue));
      },
    },
    meta1: {
      get() {
        const meta1s = map(this.units, (unit) => unit.attributes.meta1);
        const uniqmeta1s = uniq(meta1s);
        if (uniqmeta1s.length > 1) {
          return '';
        } else {
          return uniqmeta1s[0];
        }
      },
      set(value) {
        this.onChangeMeta1(value);
      },
    },
    meta2: {
      get() {
        const meta2s = map(this.units, (unit) => unit.attributes.meta2);
        const uniqmeta2s = uniq(meta2s);
        if (uniqmeta2s.length > 1) {
          return '';
        } else {
          return uniqmeta2s[0];
        }
      },
      set(value) {
        this.onChangeMeta2(value);
      },
    },
    meta1Label() {
      const meta1s = map(this.units, (unit) => unit.attributes.meta1);
      const uniqmeta1s = uniq(meta1s);
      if (uniqmeta1s.length > 1) {
        return 'Multiple values assigned';
      } else {
        return 'Meta 1';
      }
    },
    meta2Label() {
      const meta2s = map(this.units, (unit) => unit.attributes.meta2);
      const uniqmeta2s = uniq(meta2s);
      if (uniqmeta2s.length > 1) {
        return 'Multiple values assigned';
      } else {
        return 'Meta 2';
      }
    },
    moreThanOneSeatSelected() {
      return this.units.length > 1;
    },
    hasOnlyUnavailableSeatsSelected() {
      return this.units.every((u) => {
        return u.attributes.status === SEATSTATUS.OCCUPIED || u.attributes.status === SEATSTATUS.RESERVED || u.inCart;
      });
    },
    hasBookedSeatsInSelection() {
      let bookedUnits = 0;
      if (this.units.length > 0) {
        this.units.forEach((u) => {
          if (u.type === TYPE.SEAT) {
            bookedUnits +=
              u.attributes.status === SEATSTATUS.OCCUPIED || u.attributes.status === SEATSTATUS.RESERVED || u.inCart
                ? 1
                : 0;
          } else if (u.type === TYPE.TABLE) {
            const bookedTableSeats = u.seats.filter(
              (s) =>
                s.attributes.status === SEATSTATUS.OCCUPIED || s.attributes.status === SEATSTATUS.RESERVED || s.inCart
            );
            bookedUnits += bookedTableSeats.length > 0 ? 1 : 0;
          }
        });
      }
      return !!bookedUnits;
    },
    draftEvent() {
      return this.$app.props.status === EVENTSTATUS.DRAFT;
    },
    pauseEvent() {
      return this.$app.props.status === EVENTSTATUS.PAUSED;
    },
    groupedRows() {
      return this.getGroupedRows();
    },

    rowSpacingDisabled() {
      const rows = Object.values(this.groupedRows).map((seatsArray) => {
        return seatsArray.filter((seat) => seat.type === TYPE.SEAT && this.$app.seatmap.selection.items.includes(seat));
      });

      return rows.length < 2;
    },
    fullRowOrColumnSelected() {
      const fullRowsSelected = Object.values(this.groupedRows).every((seats) => {
        return seats[0].row.seats.length === seats.length;
      });

      if (fullRowsSelected) return true;

      const fullColsSelected = Object.values(this.groupedRows)
        .map((seatsArray) => {
          return seatsArray.filter(
            (seat) => seat.type === TYPE.SEAT && this.$app.seatmap.selection.items.includes(seat)
          );
        })
        .reduce((acc, seatsArray) => {
          seatsArray.forEach((seat, i) => {
            if (!acc[i]) acc[i] = [];
            acc[i].push(seat);
          });
          return acc;
        }, [])
        .every((seats) => {
          return seats[0].block.rows.length === seats.length;
        });

      return fullColsSelected;
    },
  },
  methods: {
    getRowSpacing() {
      const cols = Object.values(this.groupedRows)
        .map((seatsArray) => {
          return seatsArray.filter(
            (seat) => seat.type === TYPE.SEAT && this.$app.seatmap.selection.items.includes(seat)
          );
        })
        .reduce((acc, seatsArray) => {
          seatsArray.forEach((seat, i) => {
            if (!acc[i]) acc[i] = [];
            acc[i].push(seat);
          });
          return acc;
        }, []);

      const seatsArray = cols[0];
      const len = seatsArray.length;

      const rotate = seatsArray[0].attributes.rotation || 0;

      const seatBBox = seatsArray[0].shape.bbox();

      if (len === 1) {
        return Math.round(seatBBox.height);
      }

      // get boundingbox of all seats
      const bbox = seatsArray.reduce((acc, seat) => {
        return seat.shape.bbox().merge(acc);
      }, seatBBox);

      const cosTheta = Math.cos((rotate / 180) * Math.PI);
      const sinTheta = Math.sin((rotate / 180) * Math.PI);

      const seatHeights = seatBBox.height * len;
      const rotatedHeight = bbox.height * Math.abs(cosTheta) + bbox.width * Math.abs(sinTheta);

      const currentSpacing = (rotatedHeight - seatHeights) / (len - 1);

      return Math.round(currentSpacing);
    },
    getSeatSpacing() {
      const rows = Object.values(this.groupedRows).map((seatsArray) => {
        return seatsArray.filter((seat) => seat.type === TYPE.SEAT && this.$app.seatmap.selection.items.includes(seat));
      });

      const seatsArray = rows[0];
      const len = seatsArray.length;

      const rotate = seatsArray[0].attributes.rotation || 0;

      const seatBBox = seatsArray[0].shape.bbox();

      if (len === 1) {
        return Math.round(seatBBox.width);
      }

      // get boundingbox of all seats
      const bbox = seatsArray.reduce((acc, seat) => {
        return seat.shape.bbox().merge(acc);
      }, seatBBox);

      const cosTheta = Math.cos((rotate / 180) * Math.PI);
      const sinTheta = Math.sin((rotate / 180) * Math.PI);

      const seatWidths = seatBBox.width * len;
      const rotatedWidth = bbox.width * Math.abs(cosTheta) + bbox.height * Math.abs(sinTheta);

      const currentSpacing = (rotatedWidth - seatWidths) / (len - 1);
      return Math.round(currentSpacing);
    },
    onChangeMeta1(value) {
      this.$app.seatmap.triggerUnsavedChanges(true);
      forEach(this.units, (unit) => unit.changeMeta1(value.trim()));
    },
    onChangeMeta2(value) {
      this.$app.seatmap.triggerUnsavedChanges(true);
      forEach(this.units, (unit) => unit.changeMeta2(value.trim()));
    },
    onSkewValueChange(value) {
      // const skews = map(this.units, (unit) => unit.attributes.skew);
      // const uniqskews = uniq(skews);
      // if (uniqskews.length > 1) {
      //   this.skew.value = 0;
      // } else {
      //   this.skew.value = uniqskews[0];
      // }
      const positive = value > this.skew.value;
      const times = Math.abs(value - this.skew.value) / this.skew.interval;
      console.log(positive, times);
      eventbus.emit('selection:skew', positive, times);
      forEach(this.units, (unit) => unit.changeSkew(value));
      this.skew.value = value;
    },
    onCurveValueChange(value) {
      eventbus.emit('selection:curve', value);
      forEach(this.units, (unit) => unit.changeCurve(value));
    },
    onRowSpacingChange(value) {
      eventbus.emit('selection:rowSpacing', value);
    },
    onSeatSpacingChange(value) {
      eventbus.emit('selection:seatSpacing', value);
    },
    flipHorizontally() {
      this.units[0].block.flip(FLIP.HORIZONTALLY);

      // eventbus.emit('selection:flip', FLIP.HORIZONTALLY);
    },
    flipVertically() {
      this.units[0].block.flip(FLIP.VERTICALLY);
      // eventbus.emit('selection:flip', FLIP.VERTICALLY);
    },
    duplicate() {
      eventbus.emit('selection:clone');
    },
    removeBlocks() {
      eventbus.emit('seatmap:removeBlocks');
    },
    splitBlock() {
      this.$app.seatmap.splitBlock(this.groupedRows);
    },
    getGroupedRows() {
      return this.$app.seatmap.getGroupedRows(this.$app.seatmap.selection.items);
    },
    getNumberOfColumns() {
      return this.units[0].row.seats.length;
    },
    onRowsChange(value: number) {
      const oldValue = this.rows;
      this.rows = value;

      if (oldValue < value) {
        const addedSeats = this.units[0].row.block.initializeAndAddRows(value - oldValue, this.cols);
        this.units[0].row.block.calculateLengthsCoeffs();
        this.$app.seatmap.selection.addAll(addedSeats);
        eventbus.emit('selection:updated', this.$app.seatmap.selection);
      } else {
        const seatsToDelete = this.units[0].row.block.removeRows(oldValue - value);
        this.$app.seatmap.selection.removeAll(seatsToDelete);
        eventbus.emit('selection:updated', this.$app.seatmap.selection);
      }
    },
    onColsChange(value: number) {
      const oldValue = this.cols;
      this.cols = value;

      if (oldValue < value) {
        const addedSeats = this.units[0].row.block.initializeAndAddColumns(value - oldValue, this.cols);
        this.units[0].row.block.calculateLengthsCoeffs();
        this.$app.seatmap.selection.addAll(addedSeats);
        eventbus.emit('selection:updated', this.$app.seatmap.selection);
      } else {
        const seatsToDelete = this.units[0].row.block.removeColumns(oldValue - value);
        this.$app.seatmap.selection.removeAll(seatsToDelete);
        eventbus.emit('selection:updated', this.$app.seatmap.selection);
      }
    },
  },
};
</script>
<style lang="scss"></style>
